import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { 
  Globe, FileText, DollarSign, Star, BarChart3, Image, Mail,
  Plus, Edit, Trash2, Save, ArrowLeft, Upload, CheckCircle, X,
  Settings as SettingsIcon, Palette, Type, MessageSquare,
  Copy, Loader2, ImageIcon, Film, File, Folder, ListOrdered, 
  MousePointer, Users, Play, HelpCircle, Quote, Workflow
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import api from '../utils/api';

// CMS API helper
const cmsApi = {
  get: (url) => api.get(url, { headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` } }),
  post: (url, data) => api.post(url, data, { headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` } }),
  put: (url, data) => api.put(url, data, { headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` } }),
  delete: (url) => api.delete(url, { headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` } }),
};

// ==================== Site Settings ====================
export const SiteSettingsEditor = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await cmsApi.get('/cms/settings');
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await cmsApi.put('/cms/settings', settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // Helper to get displayable image URL with cache busting
  const getDisplayUrl = (url, addCacheBuster = false) => {
    if (!url) return '';
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    let finalUrl = url;
    
    // If it's a relative /api path, prepend backend URL
    if (url.startsWith('/api')) {
      finalUrl = `${backendUrl}${url}`;
    }
    // If it's a Google Drive link, convert to direct image URL
    else if (url.includes('drive.google.com/file/d/')) {
      const fileId = url.match(/\/d\/([^/]+)/)?.[1];
      if (fileId) {
        finalUrl = `https://drive.google.com/uc?export=view&id=${fileId}`;
      }
    }
    
    // Add cache buster for freshly uploaded images
    if (addCacheBuster) {
      const separator = finalUrl.includes('?') ? '&' : '?';
      finalUrl = `${finalUrl}${separator}t=${Date.now()}`;
    }
    
    return finalUrl;
  };

  // Track cache busters for images to force reload after upload
  const [logoCacheBuster, setLogoCacheBuster] = useState(Date.now());
  const [faviconCacheBuster, setFaviconCacheBuster] = useState(Date.now());

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    const isLogo = type === 'logo';
    if (isLogo) setUploadingLogo(true);
    else setUploadingFavicon(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      
      const response = await api.post('/cms/upload-branding', formData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('super_admin_token')}`
        }
      });
      
      const fieldName = isLogo ? 'logo_url' : 'favicon_url';
      const newSettings = { ...settings, [fieldName]: response.data.url };
      setSettings(newSettings);
      
      // Update cache buster to force image reload
      if (isLogo) setLogoCacheBuster(Date.now());
      else setFaviconCacheBuster(Date.now());
      
      // Auto-save after upload
      await cmsApi.put('/cms/settings', newSettings);
      toast.success(`${isLogo ? 'Logo' : 'Favicon'} uploaded and saved successfully`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Failed to upload ${isLogo ? 'logo' : 'favicon'}: ${error.response?.data?.detail || error.message}`);
    } finally {
      if (isLogo) setUploadingLogo(false);
      else setUploadingFavicon(false);
    }
  };

  if (loading) return <div className="p-6 text-center">Loading...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Site Settings</h1>
          <p className="text-gray-500">Configure your website's general settings</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Site Name</Label>
                <Input 
                  value={settings?.site_name || ''} 
                  onChange={(e) => handleChange('site_name', e.target.value)}
                  placeholder="Ontrof"
                />
              </div>
              <div>
                <Label>Tagline</Label>
                <Input 
                  value={settings?.tagline || ''} 
                  onChange={(e) => handleChange('tagline', e.target.value)}
                  placeholder="Travel Quotation Software"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logo & Favicon Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Branding Assets
            </CardTitle>
            <CardDescription>Upload your logo and favicon for the website</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Logo Upload */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Logo</Label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                  {settings?.logo_url ? (
                    <div className="space-y-3">
                      <img 
                        key={logoCacheBuster}
                        src={`${getDisplayUrl(settings.logo_url)}?t=${logoCacheBuster}`} 
                        alt="Logo" 
                        className="max-h-20 mx-auto object-contain"
                        onError={(e) => { 
                          e.target.onerror = null; 
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwIiB5PSI1NSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzk5OSIgZm9udC1zaXplPSIxMiI+RXJyb3I8L3RleHQ+PC9zdmc+';
                        }}
                      />
                      <div className="flex items-center justify-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={uploadingLogo}
                        >
                          {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                          Replace
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleChange('logo_url', '')}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="py-6 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => logoInputRef.current?.click()}
                    >
                      {uploadingLogo ? (
                        <Loader2 className="w-8 h-8 mx-auto text-gray-400 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">Click to upload logo</p>
                          <p className="text-xs text-gray-400 mt-1">PNG, JPG, SVG up to 2MB</p>
                        </>
                      )}
                    </div>
                  )}
                  <input 
                    type="file" 
                    ref={logoInputRef}
                    className="hidden" 
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'logo')}
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Or enter URL directly</Label>
                  <Input 
                    value={settings?.logo_url || ''} 
                    onChange={(e) => handleChange('logo_url', e.target.value)}
                    placeholder="https://..."
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Favicon Upload */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Favicon</Label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                  {settings?.favicon_url ? (
                    <div className="space-y-3">
                      <img 
                        key={faviconCacheBuster}
                        src={`${getDisplayUrl(settings.favicon_url)}?t=${faviconCacheBuster}`} 
                        alt="Favicon" 
                        className="w-16 h-16 mx-auto object-contain"
                        onError={(e) => { 
                          e.target.onerror = null; 
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwIiB5PSI1NSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzk5OSIgZm9udC1zaXplPSIxMiI+RXJyb3I8L3RleHQ+PC9zdmc+';
                        }}
                      />
                      <div className="flex items-center justify-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => faviconInputRef.current?.click()}
                          disabled={uploadingFavicon}
                        >
                          {uploadingFavicon ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                          Replace
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleChange('favicon_url', '')}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="py-6 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => faviconInputRef.current?.click()}
                    >
                      {uploadingFavicon ? (
                        <Loader2 className="w-8 h-8 mx-auto text-gray-400 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">Click to upload favicon</p>
                          <p className="text-xs text-gray-400 mt-1">ICO, PNG 32x32 or 64x64</p>
                        </>
                      )}
                    </div>
                  )}
                  <input 
                    type="file" 
                    ref={faviconInputRef}
                    className="hidden" 
                    accept="image/*,.ico"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'favicon')}
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Or enter URL directly</Label>
                  <Input 
                    value={settings?.favicon_url || ''} 
                    onChange={(e) => handleChange('favicon_url', e.target.value)}
                    placeholder="https://..."
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SEO */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Type className="w-5 h-5" />
              SEO Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Meta Title</Label>
              <Input 
                value={settings?.meta_title || ''} 
                onChange={(e) => handleChange('meta_title', e.target.value)}
                placeholder="Ontrof - Travel Quotation Software"
              />
            </div>
            <div>
              <Label>Meta Description</Label>
              <textarea
                value={settings?.meta_description || ''}
                onChange={(e) => handleChange('meta_description', e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
                rows={3}
                placeholder="Create professional travel quotations in minutes..."
              />
            </div>
            <div>
              <Label>Meta Keywords</Label>
              <Input 
                value={settings?.meta_keywords || ''} 
                onChange={(e) => handleChange('meta_keywords', e.target.value)}
                placeholder="travel, quotation, DMC, agency"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Contact Email</Label>
                <Input 
                  value={settings?.contact_email || ''} 
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  placeholder="contact@ontrof.com"
                />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input 
                  value={settings?.contact_phone || ''} 
                  onChange={(e) => handleChange('contact_phone', e.target.value)}
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <textarea
                value={settings?.contact_address || ''}
                onChange={(e) => handleChange('contact_address', e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
                rows={2}
                placeholder="Your business address"
              />
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-green-500">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div>
                <Label className="font-medium">Enable WhatsApp Button</Label>
                <p className="text-sm text-gray-500">Show floating WhatsApp chat button on website</p>
              </div>
              <Switch 
                checked={settings?.whatsapp_enabled !== false}
                onCheckedChange={(v) => handleChange('whatsapp_enabled', v)}
              />
            </div>
            <div>
              <Label>WhatsApp Phone Number</Label>
              <Input 
                value={settings?.whatsapp_number || ''} 
                onChange={(e) => handleChange('whatsapp_number', e.target.value)}
                placeholder="8530412399"
              />
              <p className="text-xs text-gray-500 mt-1">Enter phone number without country code (will auto-add +91)</p>
            </div>
          </CardContent>
        </Card>

        {/* Theme */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Theme Colors
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Primary Color</Label>
                <div className="flex gap-2">
                  <Input 
                    value={settings?.primary_color || '#3b82f6'} 
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                  />
                  <input 
                    type="color" 
                    value={settings?.primary_color || '#3b82f6'}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                </div>
              </div>
              <div>
                <Label>Secondary Color</Label>
                <div className="flex gap-2">
                  <Input 
                    value={settings?.secondary_color || '#06b6d4'} 
                    onChange={(e) => handleChange('secondary_color', e.target.value)}
                  />
                  <input 
                    type="color" 
                    value={settings?.secondary_color || '#06b6d4'}
                    onChange={(e) => handleChange('secondary_color', e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label>Copyright Text</Label>
              <Input 
                value={settings?.copyright_text || ''} 
                onChange={(e) => handleChange('copyright_text', e.target.value)}
                placeholder="2026 Ontrof. All rights reserved."
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== Hero Editor ====================
export const HeroEditor = () => {
  const [hero, setHero] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchHero();
  }, []);

  const fetchHero = async () => {
    try {
      const response = await cmsApi.get('/cms/hero');
      setHero(response.data);
    } catch (error) {
      toast.error('Failed to fetch hero content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await cmsApi.put('/cms/hero', hero);
      toast.success('Hero content saved successfully');
    } catch (error) {
      toast.error('Failed to save hero content');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setHero(prev => ({ ...prev, [field]: value }));
  };

  if (loading) return <div className="p-6 text-center">Loading...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hero Section</h1>
          <p className="text-gray-500">Edit the main hero section of your landing page</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <Label>Badge Text</Label>
            <Input 
              value={hero?.badge_text || ''} 
              onChange={(e) => handleChange('badge_text', e.target.value)}
              placeholder="Travel Quotation Software for Modern Agencies"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Headline</Label>
              <Input 
                value={hero?.headline || ''} 
                onChange={(e) => handleChange('headline', e.target.value)}
                placeholder="Create Travel Quotations"
              />
            </div>
            <div>
              <Label>Headline Highlight</Label>
              <Input 
                value={hero?.headline_highlight || ''} 
                onChange={(e) => handleChange('headline_highlight', e.target.value)}
                placeholder="10x Faster"
              />
            </div>
          </div>
          <div>
            <Label>Subheadline</Label>
            <textarea
              value={hero?.subheadline || ''}
              onChange={(e) => handleChange('subheadline', e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
              rows={3}
              placeholder="The complete SaaS platform for DMCs..."
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Primary CTA Text</Label>
              <Input 
                value={hero?.primary_cta_text || ''} 
                onChange={(e) => handleChange('primary_cta_text', e.target.value)}
                placeholder="Start Free Trial"
              />
            </div>
            <div>
              <Label>Primary CTA Link</Label>
              <Input 
                value={hero?.primary_cta_link || ''} 
                onChange={(e) => handleChange('primary_cta_link', e.target.value)}
                placeholder="/register"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Secondary CTA Text</Label>
              <Input 
                value={hero?.secondary_cta_text || ''} 
                onChange={(e) => handleChange('secondary_cta_text', e.target.value)}
                placeholder="View Demo"
              />
            </div>
            <div>
              <Label>Secondary CTA Link</Label>
              <Input 
                value={hero?.secondary_cta_link || ''} 
                onChange={(e) => handleChange('secondary_cta_link', e.target.value)}
                placeholder="/cherry-holidays/login"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== Features Manager ====================
export const FeaturesManager = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [form, setForm] = useState({ 
    icon: 'FileText', 
    title: '', 
    description: '', 
    category: '',
    tag: '',
    tag_label: '',
    tag_color: 'blue',
    display_order: 0, 
    is_active: true 
  });

  const TAG_COLORS = [
    { value: 'blue', label: 'Blue', class: 'bg-blue-100 text-blue-700 border-t-blue-500' },
    { value: 'orange', label: 'Orange', class: 'bg-orange-100 text-orange-700 border-t-orange-500' },
    { value: 'green', label: 'Green', class: 'bg-green-100 text-green-700 border-t-green-500' },
    { value: 'purple', label: 'Purple', class: 'bg-purple-100 text-purple-700 border-t-purple-500' },
    { value: 'cyan', label: 'Cyan', class: 'bg-cyan-100 text-cyan-700 border-t-cyan-500' },
    { value: 'red', label: 'Red', class: 'bg-red-100 text-red-700 border-t-red-500' },
  ];

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const response = await cmsApi.get('/cms/features');
      setFeatures(response.data);
    } catch (error) {
      toast.error('Failed to fetch features');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingFeature) {
        await cmsApi.put(`/cms/features/${editingFeature.id}`, form);
        toast.success('Feature updated');
      } else {
        await cmsApi.post('/cms/features', form);
        toast.success('Feature created');
      }
      setShowDialog(false);
      setEditingFeature(null);
      setForm({ icon: 'FileText', title: '', description: '', category: '', tag: '', tag_label: '', tag_color: 'blue', display_order: 0, is_active: true });
      fetchFeatures();
    } catch (error) {
      toast.error('Failed to save feature');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this feature?')) return;
    try {
      await cmsApi.delete(`/cms/features/${id}`);
      toast.success('Feature deleted');
      fetchFeatures();
    } catch (error) {
      toast.error('Failed to delete feature');
    }
  };

  const openEdit = (feature) => {
    setEditingFeature(feature);
    setForm({
      ...feature,
      category: feature.category || '',
      tag: feature.tag || '',
      tag_label: feature.tag_label || '',
      tag_color: feature.tag_color || 'blue'
    });
    setShowDialog(true);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Features</h1>
          <p className="text-gray-500">Manage the features displayed on your landing page</p>
        </div>
        <Button onClick={() => { setEditingFeature(null); setForm({ icon: 'FileText', title: '', description: '', category: '', tag: '', tag_label: '', tag_color: 'blue', display_order: 0, is_active: true }); setShowDialog(true); }} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Feature
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Icon</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Tag</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {features.map((feature) => (
                  <TableRow key={feature.id}>
                    <TableCell>{feature.display_order}</TableCell>
                    <TableCell><code className="text-sm bg-gray-100 px-2 py-1 rounded">{feature.icon}</code></TableCell>
                    <TableCell className="font-medium">{feature.title}</TableCell>
                    <TableCell>{feature.category || '-'}</TableCell>
                    <TableCell>
                      {feature.tag && (
                        <span className={`px-2 py-0.5 rounded text-xs bg-${feature.tag_color || 'blue'}-100 text-${feature.tag_color || 'blue'}-700`}>
                          {feature.tag}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={feature.is_active ? 'default' : 'secondary'}>
                        {feature.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(feature)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(feature.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingFeature ? 'Edit Feature' : 'Add Feature'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Icon (kebab-case)</Label>
                <Input 
                  value={form.icon} 
                  onChange={(e) => setForm(prev => ({ ...prev, icon: e.target.value }))}
                  placeholder="clipboard-clock"
                />
                <p className="text-xs text-gray-500 mt-1">e.g., clipboard-clock, indian-rupee</p>
              </div>
              <div>
                <Label>Display Order</Label>
                <Input 
                  type="number"
                  value={form.display_order} 
                  onChange={(e) => setForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Tag Color</Label>
                <Select 
                  value={form.tag_color} 
                  onValueChange={(value) => setForm(prev => ({ ...prev, tag_color: value }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select color" />
                  </SelectTrigger>
                  <SelectContent>
                    {TAG_COLORS.map(color => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <span className={`w-3 h-3 rounded-full bg-${color.value}-500`}></span>
                          {color.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Title</Label>
              <Input 
                value={form.title} 
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="10-Minute Quotation Engine"
              />
            </div>
            <div>
              <Label>Description</Label>
              <textarea
                value={form.description || ''}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md"
                rows={2}
                placeholder="A complete, priced, formatted tour quotation..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category (for tabs)</Label>
                <Input 
                  value={form.category || ''} 
                  onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="Speed & Automation"
                />
                <p className="text-xs text-gray-500 mt-1">Groups features into tabs on the frontend</p>
              </div>
              <div>
                <Label>Tag (shown on card)</Label>
                <Input 
                  value={form.tag || ''} 
                  onChange={(e) => setForm(prev => ({ ...prev, tag: e.target.value }))}
                  placeholder="Speed"
                />
              </div>
            </div>
            <div>
              <Label>Tag Label (bottom highlight)</Label>
              <Input 
                value={form.tag_label || ''} 
                onChange={(e) => setForm(prev => ({ ...prev, tag_label: e.target.value }))}
                placeholder="87% faster than manual"
              />
              <p className="text-xs text-gray-500 mt-1">Shown at the bottom of the feature card with a bullet</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={form.is_active} 
                onCheckedChange={(v) => setForm(prev => ({ ...prev, is_active: v }))}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== Pricing Manager ====================
export const PricingManager = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', price: 0, currency: 'INR', billing_period: 'month', features: [], is_popular: false, cta_text: 'Get Started', cta_link: '/register', display_order: 0, is_active: true });
  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await cmsApi.get('/cms/pricing');
      setPlans(response.data);
    } catch (error) {
      toast.error('Failed to fetch pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingPlan) {
        await cmsApi.put(`/cms/pricing/${editingPlan.id}`, form);
        toast.success('Plan updated');
      } else {
        await cmsApi.post('/cms/pricing', form);
        toast.success('Plan created');
      }
      setShowDialog(false);
      setEditingPlan(null);
      setForm({ name: '', description: '', price: 0, currency: 'INR', billing_period: 'month', features: [], is_popular: false, cta_text: 'Get Started', cta_link: '/register', display_order: 0, is_active: true });
      fetchPlans();
    } catch (error) {
      toast.error('Failed to save plan');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this plan?')) return;
    try {
      await cmsApi.delete(`/cms/pricing/${id}`);
      toast.success('Plan deleted');
      fetchPlans();
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setForm(prev => ({ ...prev, features: [...(prev.features || []), featureInput.trim()] }));
      setFeatureInput('');
    }
  };

  const removeFeature = (idx) => {
    setForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== idx) }));
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm(plan);
    setShowDialog(true);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pricing Plans</h1>
          <p className="text-gray-500">Manage your subscription pricing tiers</p>
        </div>
        <Button onClick={() => { setEditingPlan(null); setForm({ name: '', description: '', price: 0, currency: 'INR', billing_period: 'month', features: [], is_popular: false, cta_text: 'Get Started', cta_link: '/register', display_order: 0, is_active: true }); setShowDialog(true); }} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Plan
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <Card key={plan.id} className={plan.is_popular ? 'ring-2 ring-purple-500' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{plan.name}</CardTitle>
                {plan.is_popular && <Badge className="bg-purple-500">Popular</Badge>}
              </div>
              <CardDescription>{plan.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold mb-4">
                {plan.price === 0 ? 'Free' : `₹${plan.price.toLocaleString()}`}
                {plan.price > 0 && <span className="text-sm font-normal text-gray-500">/{plan.billing_period}</span>}
              </div>
              <ul className="space-y-2 mb-4">
                {(plan.features || []).slice(0, 4).map((f, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {f}
                  </li>
                ))}
                {(plan.features || []).length > 4 && (
                  <li className="text-sm text-gray-400">+{plan.features.length - 4} more</li>
                )}
              </ul>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(plan)}>
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(plan.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit Plan' : 'Add Plan'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Plan Name</Label>
                <Input 
                  value={form.name} 
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Professional"
                />
              </div>
              <div>
                <Label>Display Order</Label>
                <Input 
                  type="number"
                  value={form.display_order} 
                  onChange={(e) => setForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                value={form.description || ''} 
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="For growing agencies"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Price</Label>
                <Input 
                  type="number"
                  value={form.price} 
                  onChange={(e) => setForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Input 
                  value={form.currency} 
                  onChange={(e) => setForm(prev => ({ ...prev, currency: e.target.value }))}
                />
              </div>
              <div>
                <Label>Billing Period</Label>
                <Input 
                  value={form.billing_period} 
                  onChange={(e) => setForm(prev => ({ ...prev, billing_period: e.target.value }))}
                  placeholder="month"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CTA Text</Label>
                <Input 
                  value={form.cta_text} 
                  onChange={(e) => setForm(prev => ({ ...prev, cta_text: e.target.value }))}
                />
              </div>
              <div>
                <Label>CTA Link</Label>
                <Input 
                  value={form.cta_link} 
                  onChange={(e) => setForm(prev => ({ ...prev, cta_link: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Features</Label>
              <div className="flex gap-2 mb-2">
                <Input 
                  value={featureInput}
                  onChange={(e) => setFeatureInput(e.target.value)}
                  placeholder="Add a feature..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                />
                <Button type="button" onClick={addFeature}>Add</Button>
              </div>
              <div className="space-y-1">
                {(form.features || []).map((f, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded">
                    <span className="flex-1 text-sm">{f}</span>
                    <button onClick={() => removeFeature(idx)} className="text-gray-400 hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={form.is_popular} 
                  onCheckedChange={(v) => setForm(prev => ({ ...prev, is_popular: v }))}
                />
                <Label>Popular</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={form.is_active} 
                  onCheckedChange={(v) => setForm(prev => ({ ...prev, is_active: v }))}
                />
                <Label>Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== Contact Submissions ====================
export const ContactSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await cmsApi.get('/cms/contact-submissions');
      setSubmissions(response.data);
    } catch (error) {
      toast.error('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await cmsApi.put(`/cms/contact-submissions/${id}?status=${status}`);
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure?')) return;
    try {
      await cmsApi.delete(`/cms/contact-submissions/${id}`);
      toast.success('Deleted');
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Contact Submissions</h1>
        <p className="text-gray-500">View and manage contact form submissions</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No submissions yet</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.map((sub) => (
                  <TableRow key={sub.id}>
                    <TableCell className="font-medium">{sub.name}</TableCell>
                    <TableCell>{sub.email}</TableCell>
                    <TableCell>{sub.company || '-'}</TableCell>
                    <TableCell className="max-w-xs truncate">{sub.message}</TableCell>
                    <TableCell>
                      <Badge variant={sub.status === 'new' ? 'default' : sub.status === 'read' ? 'secondary' : 'outline'}>
                        {sub.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(sub.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleStatusChange(sub.id, 'read')}>
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(sub.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== Media Library ====================
export const MediaLibrary = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const fileInputRef = useRef(null);
  const dropzoneRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await cmsApi.get('/cms/media');
      setMedia(response.data);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadFiles(Array.from(files));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      uploadFiles(Array.from(files));
    }
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    setUploadProgress(0);
    
    let uploadedCount = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', selectedCategory === 'all' ? 'general' : selectedCategory);
        
        // Use api directly for multipart uploads
        await api.post('/cms/media/upload', formData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('super_admin_token')}`
          }
        });
        
        uploadedCount++;
        setUploadProgress(Math.round((uploadedCount / files.length) * 100));
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    if (uploadedCount > 0) {
      toast.success(`Uploaded ${uploadedCount} file(s)`);
      fetchMedia();
    }
    
    setUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (mediaId) => {
    if (!confirm('Are you sure you want to delete this file?')) return;
    try {
      await cmsApi.delete(`/cms/media/${mediaId}`);
      toast.success('File deleted');
      fetchMedia();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const copyUrl = (url) => {
    const fullUrl = window.location.origin + url;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copied to clipboard');
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return ImageIcon;
      case 'video': return Film;
      default: return File;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const categories = [
    { value: 'all', label: 'All Files' },
    { value: 'general', label: 'General' },
    { value: 'hero', label: 'Hero Images' },
    { value: 'features', label: 'Feature Icons' },
    { value: 'testimonials', label: 'Testimonials' },
    { value: 'logos', label: 'Logos' },
  ];

  const filteredMedia = selectedCategory === 'all' 
    ? media 
    : media.filter(m => m.category === selectedCategory);

  return (
    <div className="p-6 space-y-6" data-testid="media-library">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Media Library</h1>
          <p className="text-gray-500">Upload and manage images and files</p>
        </div>
        <div className="flex gap-2">
          <select 
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="h-9 px-3 rounded-md border border-gray-200 bg-white text-sm"
          >
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          <Button onClick={() => fileInputRef.current?.click()} className="bg-purple-600 hover:bg-purple-700" data-testid="upload-media-btn">
            <Upload className="w-4 h-4 mr-2" />
            Upload Files
          </Button>
        </div>
      </div>

      {/* Upload Dropzone */}
      <div
        ref={dropzoneRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragging ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,.pdf,.doc,.docx"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        {uploading ? (
          <div className="space-y-3">
            <Loader2 className="w-10 h-10 mx-auto text-purple-600 animate-spin" />
            <div className="w-48 h-2 mx-auto bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-purple-600 transition-all" 
                style={{ width: `${uploadProgress}%` }} 
              />
            </div>
            <p className="text-sm text-gray-600">Uploading... {uploadProgress}%</p>
          </div>
        ) : (
          <>
            <Image className="w-10 h-10 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 mb-1">Drag and drop files here, or click to browse</p>
            <p className="text-sm text-gray-400">Supports images, videos, and documents up to 10MB</p>
          </>
        )}
      </div>

      {/* Media Grid */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-gray-400" />
            </div>
          ) : filteredMedia.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Folder className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No files uploaded yet</p>
              <p className="text-sm">Upload your first file to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filteredMedia.map((item) => {
                const FileIcon = getFileIcon(item.file_type);
                return (
                  <div 
                    key={item.id} 
                    className="group relative border rounded-lg overflow-hidden bg-gray-50 hover:shadow-md transition-shadow"
                    data-testid={`media-item-${item.id}`}
                  >
                    {/* Preview */}
                    <div className="aspect-square flex items-center justify-center bg-gray-100">
                      {item.file_type === 'image' ? (
                        <img 
                          src={item.file_url} 
                          alt={item.alt_text || item.original_filename}
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.src = '/placeholder-image.png'}
                        />
                      ) : (
                        <FileIcon className="w-10 h-10 text-gray-400" />
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="p-2">
                      <p className="text-xs font-medium truncate" title={item.original_filename}>
                        {item.original_filename}
                      </p>
                      <p className="text-xs text-gray-400">{formatFileSize(item.file_size)}</p>
                    </div>
                    
                    {/* Hover Actions */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        className="h-8"
                        onClick={() => copyUrl(item.file_url)}
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy URL
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        className="h-8"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                    
                    {/* Category Badge */}
                    <Badge 
                      variant="secondary" 
                      className="absolute top-2 left-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {item.category || 'general'}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Stats */}
      {media.length > 0 && (
        <div className="flex gap-4 text-sm text-gray-500">
          <span>{media.length} total files</span>
          <span>{media.filter(m => m.file_type === 'image').length} images</span>
          <span>{formatFileSize(media.reduce((acc, m) => acc + (m.file_size || 0), 0))} total size</span>
        </div>
      )}
    </div>
  );
};

// ==================== FAQ Manager ====================
export const FAQManager = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    question: '',
    answer: '',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await cmsApi.get('/cms/faqs');
      setFaqs(response.data);
    } catch (error) {
      toast.error('Failed to fetch FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await cmsApi.put(`/cms/faqs/${editingItem.id}`, form);
        toast.success('FAQ updated');
      } else {
        await cmsApi.post('/cms/faqs', form);
        toast.success('FAQ created');
      }
      setShowDialog(false);
      setEditingItem(null);
      setForm({ question: '', answer: '', display_order: 0, is_active: true });
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to save FAQ');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      await cmsApi.delete(`/cms/faqs/${id}`);
      toast.success('FAQ deleted');
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      question: item.question || '',
      answer: item.answer || '',
      display_order: item.display_order || 0,
      is_active: item.is_active !== false
    });
    setShowDialog(true);
  };

  return (
    <div className="p-6 space-y-6" data-testid="faq-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">FAQs</h1>
          <p className="text-gray-500">Manage frequently asked questions for your landing page</p>
        </div>
        <Button 
          onClick={() => { setEditingItem(null); setForm({ question: '', answer: '', display_order: 0, is_active: true }); setShowDialog(true); }} 
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="add-faq-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add FAQ
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : faqs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <HelpCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No FAQs yet</p>
              <p className="text-sm">Add your first frequently asked question</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Question</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {faqs.map((faq) => (
                  <TableRow key={faq.id}>
                    <TableCell>{faq.display_order}</TableCell>
                    <TableCell className="font-medium max-w-md truncate">{faq.question}</TableCell>
                    <TableCell>
                      <Badge variant={faq.is_active ? 'default' : 'secondary'}>
                        {faq.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(faq)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(faq.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Question *</Label>
              <Input 
                value={form.question} 
                onChange={(e) => setForm(prev => ({ ...prev, question: e.target.value }))}
                placeholder="What is your return policy?"
              />
            </div>
            <div>
              <Label>Answer</Label>
              <Textarea
                value={form.answer}
                onChange={(e) => setForm(prev => ({ ...prev, answer: e.target.value }))}
                rows={4}
                placeholder="Enter the answer to the question..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Display Order</Label>
                <Input 
                  type="number"
                  value={form.display_order} 
                  onChange={(e) => setForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch 
                  checked={form.is_active} 
                  onCheckedChange={(v) => setForm(prev => ({ ...prev, is_active: v }))}
                />
                <Label>Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== Testimonials Manager ====================
export const TestimonialsManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    name: '',
    role: '',
    company: '',
    content: '',
    avatar_url: '',
    rating: 5,
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await cmsApi.get('/cms/testimonials');
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await cmsApi.put(`/cms/testimonials/${editingItem.id}`, form);
        toast.success('Testimonial updated');
      } else {
        await cmsApi.post('/cms/testimonials', form);
        toast.success('Testimonial created');
      }
      setShowDialog(false);
      setEditingItem(null);
      setForm({ name: '', role: '', company: '', content: '', avatar_url: '', rating: 5, display_order: 0, is_active: true });
      fetchItems();
    } catch (error) {
      toast.error('Failed to save testimonial');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await cmsApi.delete(`/cms/testimonials/${id}`);
      toast.success('Testimonial deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || '',
      role: item.role || '',
      company: item.company || '',
      content: item.content || '',
      avatar_url: item.avatar_url || '',
      rating: item.rating || 5,
      display_order: item.display_order || 0,
      is_active: item.is_active !== false
    });
    setShowDialog(true);
  };

  return (
    <div className="p-6 space-y-6" data-testid="testimonials-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Testimonials</h1>
          <p className="text-gray-500">Manage customer testimonials for your landing page</p>
        </div>
        <Button 
          onClick={() => { setEditingItem(null); setForm({ name: '', role: '', company: '', content: '', avatar_url: '', rating: 5, display_order: 0, is_active: true }); setShowDialog(true); }} 
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="add-testimonial-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Testimonial
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Quote className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No testimonials yet</p>
              <p className="text-sm">Add your first customer testimonial</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item) => (
                <Card key={item.id} className={!item.is_active ? 'opacity-60' : ''}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {item.avatar_url ? (
                          <img src={item.avatar_url} alt={item.name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <span className="text-purple-600 font-medium">{item.name?.charAt(0)}</span>
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-sm">{item.name}</p>
                          <p className="text-xs text-gray-500">{item.role}{item.company && `, ${item.company}`}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(item)}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-3">"{item.content}"</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-3 h-3 ${i < item.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
                        ))}
                      </div>
                      <Badge variant={item.is_active ? 'default' : 'secondary'} className="text-xs">
                        {item.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Testimonial' : 'Add Testimonial'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input 
                  value={form.name} 
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Input 
                  value={form.role} 
                  onChange={(e) => setForm(prev => ({ ...prev, role: e.target.value }))}
                  placeholder="CEO"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company</Label>
                <Input 
                  value={form.company} 
                  onChange={(e) => setForm(prev => ({ ...prev, company: e.target.value }))}
                  placeholder="Acme Corp"
                />
              </div>
              <div>
                <Label>Avatar URL</Label>
                <Input 
                  value={form.avatar_url} 
                  onChange={(e) => setForm(prev => ({ ...prev, avatar_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
            </div>
            <div>
              <Label>Content *</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
                rows={3}
                placeholder="This product is amazing..."
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Rating (1-5)</Label>
                <Input 
                  type="number"
                  min="1"
                  max="5"
                  value={form.rating} 
                  onChange={(e) => setForm(prev => ({ ...prev, rating: parseInt(e.target.value) || 5 }))}
                />
              </div>
              <div>
                <Label>Display Order</Label>
                <Input 
                  type="number"
                  value={form.display_order} 
                  onChange={(e) => setForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch 
                  checked={form.is_active} 
                  onCheckedChange={(v) => setForm(prev => ({ ...prev, is_active: v }))}
                />
                <Label>Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== How It Works Manager ====================
export const HowItWorksManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    step_number: 1,
    title: '',
    description: '',
    icon: 'Circle',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await cmsApi.get('/cms/how-it-works');
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch items');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await cmsApi.put(`/cms/how-it-works/${editingItem.id}`, form);
        toast.success('Step updated');
      } else {
        await cmsApi.post('/cms/how-it-works', form);
        toast.success('Step created');
      }
      setShowDialog(false);
      setEditingItem(null);
      setForm({ step_number: items.length + 1, title: '', description: '', icon: 'Circle', display_order: 0, is_active: true });
      fetchItems();
    } catch (error) {
      toast.error('Failed to save step');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this step?')) return;
    try {
      await cmsApi.delete(`/cms/how-it-works/${id}`);
      toast.success('Step deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete step');
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      step_number: item.step_number || 1,
      title: item.title || '',
      description: item.description || '',
      icon: item.icon || 'Circle',
      display_order: item.display_order || 0,
      is_active: item.is_active !== false
    });
    setShowDialog(true);
  };

  return (
    <div className="p-6 space-y-6" data-testid="how-it-works-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">How It Works</h1>
          <p className="text-gray-500">Manage the step-by-step process displayed on your landing page</p>
        </div>
        <Button 
          onClick={() => { setEditingItem(null); setForm({ step_number: items.length + 1, title: '', description: '', icon: 'Circle', display_order: items.length, is_active: true }); setShowDialog(true); }} 
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="add-step-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Step
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Workflow className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No steps yet</p>
              <p className="text-sm">Add your first "How It Works" step</p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.sort((a, b) => a.step_number - b.step_number).map((item) => (
                <div key={item.id} className={`flex items-start gap-4 p-4 border rounded-lg ${!item.is_active ? 'opacity-60' : ''}`}>
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-purple-600 font-bold">{item.step_number}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{item.title}</h3>
                      <Badge variant={item.is_active ? 'default' : 'secondary'} className="text-xs">
                        {item.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                    <p className="text-xs text-gray-400 mt-1">Icon: {item.icon}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(item)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Step' : 'Add Step'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Step Number *</Label>
                <Input 
                  type="number"
                  min="1"
                  value={form.step_number} 
                  onChange={(e) => setForm(prev => ({ ...prev, step_number: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div>
                <Label>Icon (Lucide name)</Label>
                <Input 
                  value={form.icon} 
                  onChange={(e) => setForm(prev => ({ ...prev, icon: e.target.value }))}
                  placeholder="Circle, Check, User, etc."
                />
              </div>
            </div>
            <div>
              <Label>Title *</Label>
              <Input 
                value={form.title} 
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Create Your Account"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                placeholder="Sign up in minutes with your email..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Display Order</Label>
                <Input 
                  type="number"
                  value={form.display_order} 
                  onChange={(e) => setForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch 
                  checked={form.is_active} 
                  onCheckedChange={(v) => setForm(prev => ({ ...prev, is_active: v }))}
                />
                <Label>Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== Videos Manager ====================
export const VideosManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    video_url: '',
    thumbnail_url: '',
    video_type: 'youtube',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await cmsApi.get('/cms/videos');
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch videos');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await cmsApi.put(`/cms/videos/${editingItem.id}`, form);
        toast.success('Video updated');
      } else {
        await cmsApi.post('/cms/videos', form);
        toast.success('Video created');
      }
      setShowDialog(false);
      setEditingItem(null);
      setForm({ title: '', description: '', video_url: '', thumbnail_url: '', video_type: 'youtube', display_order: 0, is_active: true });
      fetchItems();
    } catch (error) {
      toast.error('Failed to save video');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this video?')) return;
    try {
      await cmsApi.delete(`/cms/videos/${id}`);
      toast.success('Video deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      title: item.title || '',
      description: item.description || '',
      video_url: item.video_url || '',
      thumbnail_url: item.thumbnail_url || '',
      video_type: item.video_type || 'youtube',
      display_order: item.display_order || 0,
      is_active: item.is_active !== false
    });
    setShowDialog(true);
  };

  const getYouTubeThumbnail = (url) => {
    const match = url?.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : null;
  };

  return (
    <div className="p-6 space-y-6" data-testid="videos-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Videos</h1>
          <p className="text-gray-500">Manage video content for your landing page</p>
        </div>
        <Button 
          onClick={() => { setEditingItem(null); setForm({ title: '', description: '', video_url: '', thumbnail_url: '', video_type: 'youtube', display_order: 0, is_active: true }); setShowDialog(true); }} 
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="add-video-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Video
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Play className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No videos yet</p>
              <p className="text-sm">Add your first video</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item) => {
                const thumbnail = item.thumbnail_url || getYouTubeThumbnail(item.video_url);
                return (
                  <Card key={item.id} className={!item.is_active ? 'opacity-60' : ''}>
                    <div className="relative aspect-video bg-gray-100">
                      {thumbnail ? (
                        <img src={thumbnail} alt={item.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="w-10 h-10 text-gray-300" />
                        </div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="w-5 h-5 text-gray-800 ml-1" />
                        </div>
                      </div>
                    </div>
                    <CardContent className="pt-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm truncate">{item.title}</h3>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.description}</p>
                        </div>
                        <div className="flex gap-1 ml-2">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(item)}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDelete(item.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <Badge variant="outline" className="text-xs">{item.video_type}</Badge>
                        <Badge variant={item.is_active ? 'default' : 'secondary'} className="text-xs">
                          {item.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Video' : 'Add Video'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input 
                value={form.title} 
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Product Demo Video"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                placeholder="A quick walkthrough of our features..."
              />
            </div>
            <div>
              <Label>Video URL *</Label>
              <Input 
                value={form.video_url} 
                onChange={(e) => setForm(prev => ({ ...prev, video_url: e.target.value }))}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <p className="text-xs text-gray-500 mt-1">YouTube, Vimeo, or direct video URL</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Thumbnail URL (optional)</Label>
                <Input 
                  value={form.thumbnail_url} 
                  onChange={(e) => setForm(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                  placeholder="https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Auto-generated for YouTube</p>
              </div>
              <div>
                <Label>Video Type</Label>
                <select
                  value={form.video_type}
                  onChange={(e) => setForm(prev => ({ ...prev, video_type: e.target.value }))}
                  className="w-full h-10 px-3 border rounded-md"
                >
                  <option value="youtube">YouTube</option>
                  <option value="vimeo">Vimeo</option>
                  <option value="direct">Direct URL</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Display Order</Label>
                <Input 
                  type="number"
                  value={form.display_order} 
                  onChange={(e) => setForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch 
                  checked={form.is_active} 
                  onCheckedChange={(v) => setForm(prev => ({ ...prev, is_active: v }))}
                />
                <Label>Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== CTA Manager ====================
export const CTAManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    title: '',
    subtitle: '',
    button_text: '',
    button_link: '',
    secondary_button_text: '',
    secondary_button_link: '',
    background_color: 'blue',
    is_active: true
  });

  const BG_COLORS = [
    { value: 'blue', label: 'Blue', class: 'bg-blue-600' },
    { value: 'purple', label: 'Purple', class: 'bg-purple-600' },
    { value: 'cyan', label: 'Cyan', class: 'bg-cyan-600' },
    { value: 'green', label: 'Green', class: 'bg-green-600' },
    { value: 'navy', label: 'Navy', class: 'bg-[#0A1F44]' },
    { value: 'gradient', label: 'Gradient', class: 'bg-gradient-to-r from-purple-600 to-blue-600' },
  ];

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await cmsApi.get('/cms/cta');
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch CTAs');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await cmsApi.put(`/cms/cta/${editingItem.id}`, form);
        toast.success('CTA updated');
      } else {
        await cmsApi.post('/cms/cta', form);
        toast.success('CTA created');
      }
      setShowDialog(false);
      setEditingItem(null);
      setForm({ title: '', subtitle: '', button_text: '', button_link: '', secondary_button_text: '', secondary_button_link: '', background_color: 'blue', is_active: true });
      fetchItems();
    } catch (error) {
      toast.error('Failed to save CTA');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this CTA?')) return;
    try {
      await cmsApi.delete(`/cms/cta/${id}`);
      toast.success('CTA deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete CTA');
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      title: item.title || '',
      subtitle: item.subtitle || '',
      button_text: item.button_text || '',
      button_link: item.button_link || '',
      secondary_button_text: item.secondary_button_text || '',
      secondary_button_link: item.secondary_button_link || '',
      background_color: item.background_color || 'blue',
      is_active: item.is_active !== false
    });
    setShowDialog(true);
  };

  const getBgClass = (color) => BG_COLORS.find(c => c.value === color)?.class || 'bg-blue-600';

  return (
    <div className="p-6 space-y-6" data-testid="cta-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Call to Action</h1>
          <p className="text-gray-500">Manage CTA sections for your landing page</p>
        </div>
        <Button 
          onClick={() => { setEditingItem(null); setForm({ title: '', subtitle: '', button_text: '', button_link: '', secondary_button_text: '', secondary_button_link: '', background_color: 'blue', is_active: true }); setShowDialog(true); }} 
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="add-cta-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add CTA
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MousePointer className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No CTAs yet</p>
              <p className="text-sm">Add your first call to action</p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div 
                  key={item.id} 
                  className={`rounded-lg p-6 text-white ${getBgClass(item.background_color)} ${!item.is_active ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold">{item.title || 'Untitled CTA'}</h3>
                      {item.subtitle && <p className="text-white/80 mt-1">{item.subtitle}</p>}
                      <div className="flex gap-3 mt-4">
                        {item.button_text && (
                          <div className="px-4 py-2 bg-white text-gray-900 rounded-lg text-sm font-medium">
                            {item.button_text}
                          </div>
                        )}
                        {item.secondary_button_text && (
                          <div className="px-4 py-2 border border-white/30 rounded-lg text-sm font-medium">
                            {item.secondary_button_text}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button variant="secondary" size="sm" onClick={() => openEdit(item)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/20">
                    <Badge variant="secondary" className="text-xs">{item.background_color}</Badge>
                    <Badge variant={item.is_active ? 'default' : 'secondary'} className="text-xs">
                      {item.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit CTA' : 'Add CTA'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input 
                value={form.title} 
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ready to get started?"
              />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input 
                value={form.subtitle} 
                onChange={(e) => setForm(prev => ({ ...prev, subtitle: e.target.value }))}
                placeholder="Join thousands of happy customers today"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Primary Button Text</Label>
                <Input 
                  value={form.button_text} 
                  onChange={(e) => setForm(prev => ({ ...prev, button_text: e.target.value }))}
                  placeholder="Start Free Trial"
                />
              </div>
              <div>
                <Label>Primary Button Link</Label>
                <Input 
                  value={form.button_link} 
                  onChange={(e) => setForm(prev => ({ ...prev, button_link: e.target.value }))}
                  placeholder="/register"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Secondary Button Text</Label>
                <Input 
                  value={form.secondary_button_text} 
                  onChange={(e) => setForm(prev => ({ ...prev, secondary_button_text: e.target.value }))}
                  placeholder="Contact Sales"
                />
              </div>
              <div>
                <Label>Secondary Button Link</Label>
                <Input 
                  value={form.secondary_button_link} 
                  onChange={(e) => setForm(prev => ({ ...prev, secondary_button_link: e.target.value }))}
                  placeholder="/contact"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Background Color</Label>
                <select
                  value={form.background_color}
                  onChange={(e) => setForm(prev => ({ ...prev, background_color: e.target.value }))}
                  className="w-full h-10 px-3 border rounded-md"
                >
                  {BG_COLORS.map(color => (
                    <option key={color.value} value={color.value}>{color.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch 
                  checked={form.is_active} 
                  onCheckedChange={(v) => setForm(prev => ({ ...prev, is_active: v }))}
                />
                <Label>Active</Label>
              </div>
            </div>
            {/* Preview */}
            <div>
              <Label className="text-xs text-gray-500">Preview</Label>
              <div className={`rounded-lg p-4 text-white text-center mt-2 ${getBgClass(form.background_color)}`}>
                <p className="font-bold">{form.title || 'Title'}</p>
                {form.subtitle && <p className="text-sm text-white/80">{form.subtitle}</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== CMS Sidebar ====================
export const CMSSidebar = () => {
  const menuItems = [
    { path: '/admin/cms', icon: SettingsIcon, label: 'Site Settings' },
    { path: '/admin/cms/hero', icon: Type, label: 'Hero Section' },
    { path: '/admin/cms/features', icon: FileText, label: 'Features' },
    { path: '/admin/cms/faqs', icon: MessageSquare, label: 'FAQs' },
    { path: '/admin/cms/testimonials', icon: Users, label: 'Testimonials' },
    { path: '/admin/cms/how-it-works', icon: ListOrdered, label: 'How it Works' },
    { path: '/admin/cms/videos', icon: Film, label: 'Videos' },
    { path: '/admin/cms/cta', icon: MousePointer, label: 'Call to Action' },
    { path: '/admin/cms/pricing', icon: DollarSign, label: 'Pricing' },
    { path: '/admin/cms/media', icon: Image, label: 'Media Library' },
    { path: '/admin/cms/contact', icon: Mail, label: 'Contact Submissions' },
  ];

  return (
    <nav className="space-y-1">
      {menuItems.map((item) => (
        <Link 
          key={item.path}
          to={item.path}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-gray-500 hover:text-gray-900 transition-colors"
        >
          <item.icon className="w-5 h-5" />
          {item.label}
        </Link>
      ))}
    </nav>
  );
};

// ==================== Main CMS Router ====================
const CMSEditor = () => {
  return (
    <Routes>
      <Route path="/" element={<SiteSettingsEditor />} />
      <Route path="/hero" element={<HeroEditor />} />
      <Route path="/features" element={<FeaturesManager />} />
      <Route path="/faqs" element={<FAQManager />} />
      <Route path="/testimonials" element={<TestimonialsManager />} />
      <Route path="/how-it-works" element={<HowItWorksManager />} />
      <Route path="/videos" element={<VideosManager />} />
      <Route path="/cta" element={<CTAManager />} />
      <Route path="/pricing" element={<PricingManager />} />
      <Route path="/media" element={<MediaLibrary />} />
      <Route path="/contact" element={<ContactSubmissions />} />
    </Routes>
  );
};

export default CMSEditor;
